// Maps getUserMedia / MediaRecorder failures to clear, actionable guidance.
// The browser puts the failure reason in err.name (NOT err.message), so matching
// on err.message alone misses the common cases and shows the raw browser string
// ("The request is not allowed by the user agent or the platform...").

export interface MicErrorInfo {
  title: string
  message: string
  /** True when the user can fix it themselves via browser/OS settings. */
  actionable: boolean
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1)
}

function isSafari(): boolean {
  const ua = navigator.userAgent
  return /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua)
}

function settingsHint(): string {
  if (isIOS()) {
    return 'On iPhone/iPad: open Settings → scroll to your browser (Safari or Chrome) → turn on Microphone. Then return here and tap record again.'
  }
  if (isSafari()) {
    return 'In Safari: click Safari → Settings for This Website… (or the address-bar site menu) → set Microphone to Allow, then reload. Also check System Settings → Privacy & Security → Microphone is on for Safari.'
  }
  // Chrome / Edge / others
  return 'Click the lock/controls icon to the left of the web address → Site settings → set Microphone to Allow, then reload. On Mac, also check System Settings → Privacy & Security → Microphone is on for your browser.'
}

export function getMicErrorInfo(err: any): MicErrorInfo {
  const name = (err && (err.name || err.code)) || ''
  const msg = (err && err.message) || ''

  // Insecure context (mic requires HTTPS or localhost).
  if (typeof window !== 'undefined' && window.isSecureContext === false) {
    return {
      title: 'Secure connection required',
      message: 'Microphone capture needs a secure (https) connection. Please open the app via its https address and try again.',
      actionable: false,
    }
  }

  // Permission denied / blocked.
  if (
    name === 'NotAllowedError' ||
    name === 'PermissionDeniedError' ||
    name === 'SecurityError' ||
    /not allowed|permission|denied/i.test(msg)
  ) {
    return {
      title: 'Microphone access is blocked',
      message:
        'Your browser is blocking microphone access for this site. ' +
        settingsHint(),
      actionable: true,
    }
  }

  // No microphone device found.
  if (name === 'NotFoundError' || name === 'OverconstrainedError' || /no audio|not found|no microphone|requested device/i.test(msg)) {
    return {
      title: 'No microphone found',
      message: 'No microphone was detected. Connect or select a microphone, then try again. If you are using external/Bluetooth audio, make sure it is connected first.',
      actionable: true,
    }
  }

  // Device busy / hardware error (another app holding the mic).
  if (name === 'NotReadableError' || name === 'AbortError' || /in use|busy|could not start audio|hardware/i.test(msg)) {
    return {
      title: 'Microphone is in use',
      message: 'Your microphone could not be started — another app (Zoom, Teams, FaceTime, etc.) may be using it. Close other apps that use the mic, then try again.',
      actionable: true,
    }
  }

  // Fallback.
  return {
    title: 'Could not start recording',
    message: msg || 'Something went wrong starting the microphone. Please reload the page and try again.',
    actionable: false,
  }
}

/** Convenience: single-line string for components that only show a flat error. */
export function micErrorMessage(err: any): string {
  const info = getMicErrorInfo(err)
  return `${info.title}. ${info.message}`
}
